import { defineNuxtPlugin, useRouter } from "nuxt/app";
import { useUserProfileStore } from "@/store/userProfileStore";
import { BEAMER_TRIGGER_CSS_CLASS } from "@/constants/shared";

declare global {
  interface Window {
    Beamer?: {
      update: (config: Record<string, unknown>) => void;
    };
    beamer_config?: Record<string, unknown>;
  }
}

export default defineNuxtPlugin(() => {
  const userProfileStore = useUserProfileStore();
  const router = useRouter();
  let initialized = false;

  const initializeBeamer = (path: string) => {
    if (initialized || !userProfileStore.showBeamer) {
      return;
    }

    const config = {
      product_id: userProfileStore.beamerKey,
      user_id: `${userProfileStore.customerId}-${userProfileStore.userId}`,
      userid: userProfileStore.userId,
      user_firstname: userProfileStore.firstName || "",
      user_lastname: userProfileStore.lastName || "",
      customerid: userProfileStore.customerId,
      url: window.location.origin,
      path,
      button: false,
      selector: `.${BEAMER_TRIGGER_CSS_CLASS}`
    };

    const configScript = document.createElement("script");
    configScript.textContent = `var beamer_config = ${JSON.stringify(config)};`;

    const script = document.createElement("script");
    script.src = "https://app.getbeamer.com/js/beamer-embed.js";
    script.defer = true;

    document.head.appendChild(configScript);
    document.head.appendChild(script);
    initialized = true;
  };

  // Beamer's embed script only reads beamer_config once at load, so route changes must go through its update API.
  const updateBeamerPath = (path: string) => {
    if (!initialized || !userProfileStore.showBeamer || !window.Beamer) {
      return;
    }

    const routeAttributes = {
      url: window.location.origin,
      path
    };

    window.Beamer.update(routeAttributes);

    // beamer_config itself is never re-read by the embed script, but keep it in sync so it doesn't mislead anyone inspecting it.
    if (window.beamer_config) {
      Object.assign(window.beamer_config, routeAttributes);
    }
  };

  // afterEach fires once the route has actually changed, so `to.path` and window.location are already in sync with the new page.
  router.afterEach((to) => {
    if (!userProfileStore.showBeamer) {
      return;
    }

    initializeBeamer(to.path);
    updateBeamerPath(to.path);
  });
});